import type {
  PriceVO,
  PlanGroup,
  PlanDetailVO,
  PaymentPlan,
  BillingAccountExtended,
  Role,
  Suspend,
  EmailPlan,
  AddOnsVO,
} from '@ctct/domain-layer';
import { ADD_ON_IDS, PlanTypesEnum, SocialStarterPlanTypesEnum, EventsV1PlanTypesEnum } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { PlanPriceBreakdown } from '@ctct/payflow-ui';
import type { EmailPlansPageStore } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { emailPlansEventBus } from '../EmailPlansEventBus';
import type { EmailPlansUrlAdapter } from '../types';

export class EmailPlansManagerService {
  private readonly VISION_6_PLAN_ID = '3333_vision6';

  private isSuspended: boolean;

  constructor(
    private pageStore: EmailPlansPageStore,
    private planGroupQuery: PlanGroup,
    private planDetail: PlanDetailVO,
    private t: TranslationGetter,
    private urlAdapter: EmailPlansUrlAdapter,
    private paymentPlanQuery: PaymentPlan,
    private extendedBillingAccountQuery: BillingAccountExtended,
    private userRole: Role,
    private isCanceled?: boolean,
    private initialPlanType?: PlanTypesEnum,
    private hasScheduledDowngrade?: boolean,
    private isMWebVariantEnabled?: boolean,
    private suspend?: Suspend | null,
    private hasStripeOverdue?: boolean,
    private hasCharmPricing?: boolean,
    private isValueFocusedMWebPlanSelectionEnabled?: boolean,
    private addOnsQuery?: AddOnsVO,
    private isSmsBundle = false,
    private isSingleBillChildAccount?: boolean,
    private isMicroEmail = false,
    private microEmail3PlGroupId?: number,
    private isPlanRestructureExperiment = false,
    private shouldIncludeNon3plPlans = false
  ) {
    this.isSuspended = Boolean(this.suspend);
  }

  // True when the user is a paid (non-trial) Micro Email customer viewing the group 109 override.
  // microEmail3PlGroupId is only set by useEmailPlansWidget for paid users, so null implies trial.
  private get isPaidMicroEmail(): boolean {
    return this.isMicroEmail && this.microEmail3PlGroupId != null;
  }

  // True when any plan group override is active — either paid Micro Email or a legacy Stripe account
  // that needs to view 3PL plans (Bronze/Silver/Gold) instead of its own legacy plan types.
  private get hasPlanGroupOverride(): boolean {
    return this.microEmail3PlGroupId != null;
  }

  public api() {
    return {
      getSelectedPlanId: () => {
        return this.pageStore.getState().selectedEmailPlanId;
      },
      setSelectedPlanId: (selectedPlanId: number) => {
        this.pageStore.setState({ selectedEmailPlanId: selectedPlanId });
      },
      setEmailPlanId: (id: number | '3333_vision6', source: TEventSource) => {
        if (id === this.VISION_6_PLAN_ID) {
          emailPlansEventBus.emit('vision6PlanClicked', { source: 'user' });

          return;
        }

        this.setEmailPlanId(id, source);
      },
      getCurrentPlanId: () => {
        // Use preSuspendPlanId if available (when suspended), otherwise use planGroupQuery currentPlanId
        if (this.hasPlanGroupOverride) {
          return this.planGroupQuery.getCurrentPlanId();
        }

        return this.suspend?.getPreSuspendPlanId() ?? this.planGroupQuery.getCurrentPlanId();
      },
      getAllPlans: () => {
        return this.planGroupQuery.getAllPlans();
      },
      getEmailPlanGroupId: (): number => {
        // When a plan group override is active (paid Micro Email or legacy Stripe account),
        // Plans & Pricing shows 3PL plans from the override group, so checkout must use that
        // group ID. hasPlanGroupOverride guarantees microEmail3PlGroupId != null, but the ??
        // fallback makes the return type unambiguously number for TypeScript.
        return this.hasPlanGroupOverride
          ? this.microEmail3PlGroupId ?? this.planDetail.getPlanGroupId()
          : this.suspend?.getPreSuspendPlanGroupId() ?? this.planDetail.getPlanGroupId();
      },
      getIsTrialer: () => {
        return this.planGroupQuery.isTrialer();
      },
      showEmailPricingTierDialog: () => {
        this.showEmailPricingTierDialog();
      },
      hideEmailPricingTierDialog: () => {
        this.hideEmailPricingTierDialog();
      },
      getSelectedPlanPricingTierData: () => {
        const tiers = this.planGroupQuery.getPriceTiers();

        return tiers;
      },
      selectCurrentPlan: () => {
        this.setEmailPlanId(this.planGroupQuery.getCurrentPlanId(), 'api');
      },
    };
  }

  public data() {
    return {
      getIsDisabled: () =>
        this.extendedBillingAccountQuery.shouldBlockBilling() ||
        !this.userRole.canBuyProducts() ||
        this.hasScheduledDowngrade ||
        this.hasStripeOverdue ||
        this.isSingleBillChildAccount,
      getPricingTierData: () => this.planGroupQuery.getPriceTiers(),
      getPriceTiersForPlan: (planId: number) => this.planGroupQuery.getPriceTiersForPlan(planId),
      getCurrentPlan: () => this.planGroupQuery.getCurrentPlan(),
      getAllDisplayedPlans: () => this.planGroupQuery.getAllDisplayedPlans(),
      getCurrency: () => this.extendedBillingAccountQuery.getCurrency(),
      getSelectedEmailPlanId: () => this.pageStore.getState().selectedEmailPlanId,
      getIsMWebVariantEnabled: () => this.isMWebVariantEnabled,
      getIsValueFocusedMWebPlanSelectionEnabled: () => this.isValueFocusedMWebPlanSelectionEnabled,
      getIsSmsBundle: () => this.isSmsBundle,
      getIsSocialStarter: () => this.planGroupQuery.isSocialStarter(),
      getIsCurrentPlanEvents: () => this.planGroupQuery.isCurrentPlanEvents(),
      getContent: (selectedContactTierMin: number, prepay: string, smsBundleTierOption: string | null) => {
        const allDisplayedPlans = this.planGroupQuery.getAllDisplayedPlans();
        // Trial Micro Email users see only the Starter (BRONZE) plan.
        // Use planDetail.isInTrial() (from getPlanDetail) rather than planGroupQuery.isTrialer()
        // because when a paid Micro Email user fetches the override group 109, the backend
        // may mark their current plan there as TRIAL (since they don't own a group-109 plan),
        // which would cause isTrialer() to return true even for paid users.
        // Override: if isPaidMicroEmail is true (promotion confirmed 3PL eligibility), always
        // treat the user as paid — planDetail.isInTrial() can be incorrectly true for Stripe
        // users whose Stripe trial status hasn't been cleared after subscription conversion.
        const isMicroEmailTrialer = this.isMicroEmail && this.planDetail.isInTrial() && !this.isPaidMicroEmail;
        const plans = isMicroEmailTrialer
          ? allDisplayedPlans.filter((plan) => plan.getType() === PlanTypesEnum.Bronze)
          : allDisplayedPlans;
        const currencySymbol = this.extendedBillingAccountQuery.getCurrencySymbol();
        const currencyCode = this.extendedBillingAccountQuery.getCurrency();
        // SMS bundle plan-card decorations (the "SMS included" tag, the bundled price line,
        // and the "+ SMS" CTA) only apply when the customer has an SMS tier selected.
        // After they Remove the add-on from the order summary, all of these should disappear
        // and the cards should look like a non-bundle plan-selection.
        const isSmsBundleSelected = this.isSmsBundle && !!smsBundleTierOption;

        // Generate plan options
        const createPlanOptions = (plan: EmailPlan) => {
          const type = plan.getType();

          const isDisplayable = plan.isDisplayableOnPlansPage({
            includeNon3pl: this.shouldIncludeNon3plPlans,
          });
          const isSocialStarterType =
            this.planGroupQuery.isSocialStarter() &&
            (type === SocialStarterPlanTypesEnum.Bronze || type === SocialStarterPlanTypesEnum.Silver);
          const isEventsV1Type =
            type === EventsV1PlanTypesEnum.Bronze ||
            type === EventsV1PlanTypesEnum.Silver ||
            type === EventsV1PlanTypesEnum.Gold;

          if (!isDisplayable && !isSocialStarterType && !isEventsV1Type) return null;

          // For SocialStarter and EventsV1 plans, reuse the 3PL copy for Lite/Standard/Premium tiles
          // Type narrowed to only the 3PL plan types that have mweb-variant translations
          type ThreePLType = PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold;

          let baseType: ThreePLType;

          if (isSocialStarterType && type === SocialStarterPlanTypesEnum.Bronze) {
            baseType = PlanTypesEnum.Bronze;
          } else if (isSocialStarterType && type === SocialStarterPlanTypesEnum.Silver) {
            baseType = PlanTypesEnum.Silver;
          } else if (isEventsV1Type && type === EventsV1PlanTypesEnum.Bronze) {
            baseType = PlanTypesEnum.Bronze;
          } else if (isEventsV1Type && type === EventsV1PlanTypesEnum.Silver) {
            baseType = PlanTypesEnum.Silver;
          } else if (isEventsV1Type && type === EventsV1PlanTypesEnum.Gold) {
            baseType = PlanTypesEnum.Gold;
          } else if (type === PlanTypesEnum.LegacyGood) {
            baseType = PlanTypesEnum.Bronze;
          } else if (type === PlanTypesEnum.LegacyBetter) {
            baseType = PlanTypesEnum.Silver;
          } else if (type === PlanTypesEnum.LegacyBest) {
            baseType = PlanTypesEnum.Gold;
          } else {
            baseType = type as ThreePLType;
          }

          if (this.planDetail.isAuOrNz() && baseType === PlanTypesEnum.Gold && this.planGroupQuery.isTrialer()) {
            // replace Premium plan with Vision6 plan for Australia or New Zealand when in trial
            return this.createVision6PlanContent();
          }

          const contactTierToUse = plan.getTierByContactsAmount(selectedContactTierMin);

          if (!contactTierToUse) return null;

          const discountPercentage = this.paymentPlanQuery.getDiscountPercentage(prepay);
          let priceAfterDiscountPercent = contactTierToUse.price.substractPercentage(discountPercentage);

          const smsListPrice =
            this.isSmsBundle && smsBundleTierOption && this.addOnsQuery
              ? this.getSmsAddonPriceForTier(smsBundleTierOption)
              : undefined;

          if (smsListPrice) {
            priceAfterDiscountPercent = priceAfterDiscountPercent.add(
              smsListPrice.substractPercentage(discountPercentage)
            );
          }

          const listPriceBeforePrepayDiscount = smsListPrice
            ? contactTierToUse.price.add(smsListPrice)
            : contactTierToUse.price;

          const { value: integer, valueDecimal: decimal } = priceAfterDiscountPercent.split();
          const planId = plan.getId();
          const planDisplayName = isSocialStarterType
            ? plan.getPlanName()
            : this.getEmailPlanName(planId, isEventsV1Type);

          const priceBreakdown =
            isSmsBundleSelected && smsBundleTierOption
              ? this.getSmsBundlePriceBreakdown({
                  planDisplayName,
                  planType: baseType,
                  planPrice: contactTierToUse.price,
                  smsTierOption: smsBundleTierOption,
                  discountPercentage,
                  currencyCode,
                })
              : undefined;
          const tagLabel = this.planGroupQuery.isCurrent(planId) ? this.t('application-layer.email-plans.current') : '';
          const currentPlanId = this.planGroupQuery.getCurrentPlanId();
          let buttonText = '';
          let mWebVariantContent;

          const areSelectButtonLabels =
            this.planGroupQuery.isTrialer() ||
            this.isCanceled ||
            this.isSuspended ||
            this.planGroupQuery.isCurrentPlanFree() ||
            // When a plan group override is active (paid Micro Email or legacy Stripe account),
            // the user is viewing plans from an override group they don't own yet.
            // The override group's currentPlanId is null/unknown for them, so sequence-number
            // comparison would fall through to "Choose Plan". Force select-style labels.
            this.hasPlanGroupOverride;

          if (areSelectButtonLabels) {
            buttonText = this.getCtaButtonText(
              { context: 'select' },
              baseType,
              isSocialStarterType,
              isEventsV1Type,
              isSmsBundleSelected
            );
          } else if (this.planGroupQuery.isCurrent(planId)) {
            buttonText = this.t('application-layer.email-plans.plan-tiles.current');
          } else {
            // Compare based on plan sequence number (0 = lowest, higher = upgrade)
            const planSeqNo = this.planGroupQuery.getSequenceNumberByPlanId(planId);
            const currentSeqNo = this.planGroupQuery.getSequenceNumberByPlanId(currentPlanId);

            if (planSeqNo !== undefined && currentSeqNo !== undefined && planSeqNo < currentSeqNo) {
              buttonText = this.t('application-layer.email-plans.plan-tiles.downgrade');
            } else if (planSeqNo !== undefined && currentSeqNo !== undefined && planSeqNo > currentSeqNo) {
              buttonText = this.t('application-layer.email-plans.plan-tiles.upgrade');
            } else {
              buttonText = this.getCtaButtonText(
                { context: 'choose' },
                baseType,
                isSocialStarterType,
                isEventsV1Type,
                isSmsBundleSelected
              );
            }
          }

          const isLitePlanRestructureCapExceeded =
            this.isPlanRestructureExperiment && baseType === PlanTypesEnum.Bronze && selectedContactTierMin >= 5000;

          const isRecommendedPlan = planId === this.planGroupQuery.getRecommendedPlan()?.getId();
          const isPreSuspendPlan = planId === this.suspend?.getPreSuspendPlanId();

          const isHighlighted = isRecommendedPlan || isPreSuspendPlan;

          const bottomDescription = this.getBottomDescription(baseType, isSocialStarterType, isEventsV1Type);

          if (this.isMWebVariantEnabled) {
            let variantKey: 'plan-restructure-variant' | 'value-focused-mweb-variant' | 'mweb-variant';

            if (this.isPlanRestructureExperiment) {
              variantKey = 'plan-restructure-variant';
            } else if (this.isValueFocusedMWebPlanSelectionEnabled) {
              variantKey = 'value-focused-mweb-variant';
            } else {
              variantKey = 'mweb-variant';
            }

            const bullets = this.getMWebVariantBullets(variantKey, baseType);

            const isLiteRestructure = this.isPlanRestructureExperiment && baseType === PlanTypesEnum.Bronze;
            const usesValueFocusedLayout =
              this.isValueFocusedMWebPlanSelectionEnabled || this.isPlanRestructureExperiment;

            const includesLabel = this.getMWebVariantIncludesLabel(variantKey, baseType);

            mWebVariantContent = {
              heading: this.t(`application-layer.email-plans.${variantKey}.${baseType}.heading`),
              bullets,
              buttonText: this.getCtaButtonText(
                { context: 'mwebVariant', variantKey },
                baseType,
                isSocialStarterType,
                isEventsV1Type,
                isSmsBundleSelected
              ),
              priority: Number(this.t(`application-layer.email-plans.${variantKey}.${baseType}.priority`)),
              showIntegrationsBadge:
                this.isValueFocusedMWebPlanSelectionEnabled &&
                !this.isPlanRestructureExperiment &&
                baseType === PlanTypesEnum.Bronze,
              showPopularBanner: usesValueFocusedLayout && isHighlighted,
              startingAtLabel: usesValueFocusedLayout
                ? this.t('application-layer.email-plans.value-focused-mweb-variant.startingAtLabel')
                : undefined,
              includesLabel,
              contactCapLabel: isLiteRestructure
                ? this.t('application-layer.email-plans.plan-restructure-variant.liteContactCap')
                : undefined,
              contactCapExceededMessage: isLitePlanRestructureCapExceeded
                ? this.t('application-layer.email-plans.plan-restructure-variant.liteContactCapExceeded')
                : undefined,
            };
          }

          const buttonType: 'primary' | 'secondary' = isHighlighted ? 'primary' : 'secondary';

          // Hide default highlightedText when value-focused mWeb variant uses popular banner
          const showDefaultHighlightedText =
            isHighlighted && !isSocialStarterType && !this.isValueFocusedMWebPlanSelectionEnabled;

          return {
            id: planId.toString(),
            highlighted: isHighlighted && !isSocialStarterType,
            highlightedText: showDefaultHighlightedText
              ? this.t('application-layer.email-plans.recommended')
              : undefined,
            buttonType,
            tag: tagLabel,
            secondaryTagText: undefined,
            value: planDisplayName,
            disabled: this.planGroupQuery.isCurrent(planId) || isLitePlanRestructureCapExceeded,
            bottomDescription,
            contactCapLabel:
              this.isPlanRestructureExperiment && baseType === PlanTypesEnum.Bronze && !isLitePlanRestructureCapExceeded
                ? this.t('application-layer.email-plans.plan-restructure-variant.liteContactCap')
                : undefined,
            contactCapExceededMessage: isLitePlanRestructureCapExceeded
              ? this.t('application-layer.email-plans.plan-restructure-variant.liteContactCapExceeded')
              : undefined,
            buttonText,
            price: {
              currency: currencySymbol,
              value: integer,
              valueDecimal: this.hasCharmPricing && decimal === '00' ? undefined : decimal,
              period: this.t('application-layer.email-plans.plan-period'),
              periodMobile: this.t('application-layer.email-plans.plan-period-mobile'),
              oldValue: discountPercentage ? `${listPriceBeforePrepayDiscount.priceAmount}` : undefined,
            },
            priceBreakdown,
            mWebVariantContent,
          };
        };

        // Map all plans to options and filter out the non-displayable ones
        return plans
          .map((plan) => createPlanOptions(plan))
          .filter((option): option is NonNullable<typeof option> => option !== null);
      },
      getContactSalesLink: () => {
        return `https://www${envs.getEnv()}constantcontact.com/about/contact-us?contact=sales`;
      },
      getPopularBannerText: () => {
        return this.t('application-layer.email-plans.popular-banner');
      },
    };
  }

  public handlers() {
    return {
      onEmailPricingTierDialogClose: () => {
        this.hideEmailPricingTierDialog();
      },
      onSelectPlanClick: (selectedOptionId: string) => {
        if (selectedOptionId === this.VISION_6_PLAN_ID) {
          emailPlansEventBus.emit('vision6PlanClicked', { source: 'user' });

          return;
        }

        const nextPlanId = parseInt(selectedOptionId, 10);

        if (this.isSingleBillChildAccount && nextPlanId !== this.planGroupQuery.getCurrentPlanId()) {
          return;
        }

        this.setEmailPlanId(nextPlanId, 'user');
      },
      onRemoveSmsClick: () => {
        emailPlansEventBus.emit('smsBundleRemoved', { source: 'user' });
      },
    };
  }

  // ---------
  // Lifecycle
  // ---------

  public onMount() {
    const { emailPlanType, emailPlanId, deleteEmailPlanId } = this.urlAdapter.getEmailParams();

    if (this.isSingleBillChildAccount) {
      this.setEmailPlanId(this.planGroupQuery.getCurrentPlanId(), 'onMount');

      if (emailPlanId) {
        deleteEmailPlanId();
      }

      return;
    }

    if (emailPlanId && !Number.isNaN(Number(emailPlanId))) {
      // when passing URL params between payflow and secure we can use emailPlanId instead of emailPlanType
      this.setEmailPlanId(Number(emailPlanId), 'onMount');
      deleteEmailPlanId();
    } else if (emailPlanType) {
      // when passing URL params between FOS and payflow we can emailPlanType [i.e. planType=SILVER or planType=GOLD]
      const selectedPlan = this.planGroupQuery.getPlanByType(emailPlanType);

      if (selectedPlan) {
        this.setEmailPlanId(selectedPlan.getId(), 'onMount');
      }

      return;
    }

    const { selectedEmailPlanId } = this.pageStore.getState();

    if (this.initialPlanType && !selectedEmailPlanId) {
      const selectedPlan = this.planGroupQuery.getPlanByType(this.initialPlanType);

      if (selectedPlan) {
        this.setEmailPlanId(selectedPlan.getId(), 'onMount');
      }
    }
  }

  // ---------------
  // Private methods
  // ---------------

  private setEmailPlanId(id: number, source: TEventSource) {
    if (this.isSingleBillChildAccount && source !== 'asyncResponse' && id !== this.planGroupQuery.getCurrentPlanId()) {
      return;
    }

    const previousPlanId = this.pageStore.getState().selectedEmailPlanId;

    this.pageStore.setState({ selectedEmailPlanId: id });
    emailPlansEventBus.emit(
      'emailPlanChanged',
      {
        planId: id,
        planName: this.getEmailPlanName(id),
        previousPlanId,
      },
      { source }
    );
  }

  /**
   * Returns the bullet list to render for an mWeb plan card.
   */
  private getMWebVariantBullets(
    variantKey: 'value-focused-mweb-variant' | 'mweb-variant' | 'plan-restructure-variant',
    baseType: PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold
  ): string[] {
    const planRestructureBulletCount: Record<PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold, number> =
      {
        [PlanTypesEnum.Bronze]: 5,
        [PlanTypesEnum.Silver]: 5,
        [PlanTypesEnum.Gold]: 6,
      };

    let bulletCount: number;

    if (this.isPlanRestructureExperiment) {
      bulletCount = planRestructureBulletCount[baseType];
    } else if (this.isValueFocusedMWebPlanSelectionEnabled) {
      bulletCount = 5;
    } else {
      bulletCount = 3;
    }

    return Array.from({ length: bulletCount }, (_, idx) =>
      this.t(`application-layer.email-plans.${variantKey}.${baseType}.bullets.${idx as 0 | 1 | 2 | 3 | 4 | 5}`)
    );
  }

  private getMWebVariantIncludesLabel(
    variantKey: 'value-focused-mweb-variant' | 'mweb-variant' | 'plan-restructure-variant',
    baseType: PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold
  ): string | undefined {
    if (variantKey === 'plan-restructure-variant') {
      return this.t(`application-layer.email-plans.plan-restructure-variant.${baseType}.includesLabel`);
    }

    if (variantKey === 'value-focused-mweb-variant') {
      return this.t(`application-layer.email-plans.value-focused-mweb-variant.${baseType}.includesLabel`);
    }

    return undefined;
  }

  private getSmsAddonPriceForTier(tierMax: string) {
    const addon = this.addOnsQuery?.getAddOns().find((a) => a.getId() === ADD_ON_IDS.SMS);
    const tier = addon?.priceTiers.find((t) => `${t.tierMax}` === tierMax);

    return tier?.price;
  }

  private getSmsBundlePriceBreakdown({
    planDisplayName,
    planType,
    planPrice,
    smsTierOption,
    discountPercentage,
    currencyCode,
  }: {
    planDisplayName: string;
    planType: PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold;
    planPrice: PriceVO;
    smsTierOption: string;
    discountPercentage: number;
    currencyCode: string;
  }): PlanPriceBreakdown | undefined {
    const addon = this.addOnsQuery?.getAddOns().find((a) => a.getId() === ADD_ON_IDS.SMS);
    const tier = addon?.priceTiers.find((t) => `${t.tierMax}` === smsTierOption);

    if (!tier) {
      return undefined;
    }

    const isGoldFreeSmsTier = planType === PlanTypesEnum.Gold && tier.tierMin === 0 && tier.tierMax === 500;

    if (isGoldFreeSmsTier || tier.price.priceAmount === 0) {
      return {
        includedLabel: this.t('application-layer.email-plans.sms-bundle-breakdown.included'),
      };
    }

    const moAbbrev = this.t('application-layer.email-plans.per-month-abbrev-mobile');

    const formatBreakdownPrice = (price: PriceVO) => {
      const formattedPrice = price.substractPercentage(discountPercentage).format(currencyCode);

      return `${formattedPrice}${moAbbrev}`;
    };

    return {
      rows: [
        {
          id: 'plan',
          variant: 'plan',
          label: this.t('application-layer.email-plans.sms-bundle-breakdown.plan-line', {
            planName: planDisplayName,
          }),
          price: formatBreakdownPrice(planPrice),
        },
        {
          id: 'sms',
          variant: 'sms',
          label: this.t('application-layer.email-plans.sms-bundle-breakdown.sms-line'),
          price: formatBreakdownPrice(tier.price),
        },
      ],
      removeSmsLabel: this.t('application-layer.email-plans.sms-bundle-breakdown.remove'),
      removeSmsDataAttributes: { 'data-qe-id': 'plan-card-remove-sms' },
    };
  }

  private getEmailPlanName(id: number, isEventsV1Type = false) {
    const plan = this.planGroupQuery.getPlanById(id);
    const type = plan?.getType();
    const planName = plan?.getPlanName() ?? '';

    if (isEventsV1Type) {
      return planName.replace('Constant Contact - ', '');
    }

    // For Micro Email, use the backend plan name rather than the translation key which maps
    // BRONZE to "Lite". Strip the "Constant Contact - " product prefix so the tile shows
    // just "Starter" (e.g. "Constant Contact - Starter" → "Starter").
    if (this.isMicroEmail && type === PlanTypesEnum.Bronze) {
      return (plan?.getPlanName() ?? '').replace('Constant Contact - ', '');
    }

    if (
      type !== 'BRONZE' &&
      type !== 'SILVER' &&
      type !== 'GOLD' &&
      type !== 'LITE' &&
      type !== 'GROWTH' &&
      type !== 'GOOD' &&
      type !== 'BETTER' &&
      type !== 'BEST'
    ) {
      return planName;
    }

    return this.t(`application-layer.email-plans.plans-map.${type}`);
  }

  private getCtaButtonText(
    context:
      | { context: 'select' }
      | { context: 'choose' }
      | {
          context: 'mwebVariant';
          variantKey: 'value-focused-mweb-variant' | 'mweb-variant' | 'plan-restructure-variant';
        },
    baseType: PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold,
    isSocialStarterType: boolean,
    isEventsV1Type: boolean,
    isSmsBundleSelected: boolean
  ) {
    // Only render the "+ SMS" CTA when the user actually has an SMS tier selected;
    // after Remove in bundle mode the cards fall back to the standard plan-selection CTAs.
    if (isSmsBundleSelected) {
      return this.getSmsBundleSelectCtaLabel(baseType, isSocialStarterType);
    }

    if (isEventsV1Type) {
      return this.getEventsSelectCtaLabel(baseType, isSocialStarterType);
    }

    if (context.context === 'select') {
      return this.getSelectCtaLabel(baseType, isSocialStarterType);
    }

    if (context.context === 'choose') {
      return this.t('application-layer.email-plans.plan-tiles.choose-plan');
    }

    return this.t(`application-layer.email-plans.${context.variantKey}.${baseType}.buttonText`);
  }

  private getSmsBundleSelectCtaLabel(baseType: PlanTypesEnum, isSocialStarterType: boolean) {
    if (isSocialStarterType && baseType === PlanTypesEnum.Silver) {
      return this.t('application-layer.email-plans.sms-bundle-cta.SOCIAL-PRO');
    }

    if (isSocialStarterType && baseType === PlanTypesEnum.Bronze) {
      return this.t('application-layer.email-plans.sms-bundle-cta.SOCIAL-LITE');
    }

    if (baseType === PlanTypesEnum.Bronze) {
      return this.t('application-layer.email-plans.sms-bundle-cta.BRONZE');
    }

    if (baseType === PlanTypesEnum.Silver) {
      return this.t('application-layer.email-plans.sms-bundle-cta.SILVER');
    }

    if (baseType === PlanTypesEnum.Gold) {
      return this.t('application-layer.email-plans.sms-bundle-cta.GOLD');
    }

    observabilityAppService.notify(new Error('Unexpected baseType in getSmsBundleSelectCtaLabel'), {
      baseType,
      isSocialStarterType,
    });

    return 'NO NAME';
  }

  private getSelectCtaLabel(baseType: PlanTypesEnum, isSocialStarterType: boolean) {
    if (isSocialStarterType && baseType === PlanTypesEnum.Silver) {
      // For Social Starter "Pro" plan, use a Pro-specific label instead of "Select Standard"
      return this.t('application-layer.email-plans.social-plan-tiles.PRO.cta-label');
    }

    // "Select Starter" only applies to trial Micro Email users viewing their own group (379).
    // Paid Micro Email users see group 109 plans where BRONZE is "Lite", so they fall through
    // to the standard "Select Lite" label below.
    if (this.isMicroEmail && !this.isPaidMicroEmail && baseType === PlanTypesEnum.Bronze) {
      return this.t('application-layer.email-plans.plan-tiles.MICRO_EMAIL_STARTER.cta-label');
    }

    if (baseType === PlanTypesEnum.Bronze) {
      return this.t('application-layer.email-plans.plan-tiles.BRONZE.cta-label');
    }

    if (baseType === PlanTypesEnum.Silver) {
      return this.t('application-layer.email-plans.plan-tiles.SILVER.cta-label');
    }

    if (baseType === PlanTypesEnum.Gold) {
      return this.t('application-layer.email-plans.plan-tiles.GOLD.cta-label');
    }

    observabilityAppService.notify(new Error('Unexpected baseType in getSelectCtaLabel'), {
      baseType,
      isSocialStarterType,
    });

    return 'NO NAME';
  }

  private getEventsSelectCtaLabel(
    baseType: PlanTypesEnum.Bronze | PlanTypesEnum.Silver | PlanTypesEnum.Gold,
    isSocialStarterType: boolean
  ) {
    if (baseType === PlanTypesEnum.Bronze) {
      return this.t('application-layer.email-plans.events-plan-tiles.BRONZE.cta-label');
    }

    return this.getSelectCtaLabel(baseType, isSocialStarterType);
  }

  private getBottomDescription(baseType: PlanTypesEnum, isSocialStarterType: boolean, isEventsV1Type: boolean) {
    if (isSocialStarterType && baseType === PlanTypesEnum.Bronze) {
      return {
        // For Social Starter, do not show a heading (h6) in the description area
        heading: '',
        description: this.t('application-layer.email-plans.social-plan-tile-descriptions.LITE.description'),
      };
    }

    if (isSocialStarterType && baseType === PlanTypesEnum.Silver) {
      return {
        // For Social Starter, do not show a heading (h6) in the description area
        heading: '',
        description: this.t('application-layer.email-plans.social-plan-tile-descriptions.PRO.description'),
      };
    }

    if (isEventsV1Type && baseType === PlanTypesEnum.Bronze) {
      return {
        heading: this.t('application-layer.email-plans.events-plan-tile-descriptions.BRONZE.heading'),
        description: this.t('application-layer.email-plans.events-plan-tile-descriptions.BRONZE.description'),
      };
    }

    if (baseType === PlanTypesEnum.Bronze) {
      return {
        heading: this.t('application-layer.email-plans.plan-tile-descriptions-v3.BRONZE.heading'),
        description: this.t('application-layer.email-plans.plan-tile-descriptions-v3.BRONZE.description'),
      };
    }

    if (baseType === PlanTypesEnum.Silver) {
      return {
        heading: this.t('application-layer.email-plans.plan-tile-descriptions-v3.SILVER.heading'),
        description: this.t('application-layer.email-plans.plan-tile-descriptions-v3.SILVER.description'),
      };
    }

    if (baseType === PlanTypesEnum.Gold) {
      return {
        heading: this.t('application-layer.email-plans.plan-tile-descriptions-v3.GOLD.heading'),
        description: this.t('application-layer.email-plans.plan-tile-descriptions-v3.GOLD.description'),
      };
    }

    observabilityAppService.notify(new Error('Unexpected baseType in getBottomDescription'), {
      baseType,
      isSocialStarterType,
    });

    return {
      heading: '-',
      description: '-',
    };
  }

  private hideEmailPricingTierDialog() {
    this.pageStore.setState({ showEmailPricingTierDialog: false });
  }

  private showEmailPricingTierDialog() {
    this.pageStore.setState({ showEmailPricingTierDialog: true });
  }

  private createVision6PlanContent() {
    return {
      id: this.VISION_6_PLAN_ID,
      buttonType: 'secondary' as const,
      value: 'Vision6',
      bottomDescription: {
        heading: 'Best for the public sector',
        description:
          'Communications built for the public sector with compliance backed by Australian data storage and ISO 27001:2022 compliance.',
      },
      buttonText: 'Request a quote',
    };
  }
}
